"use client";

import SignInSignUpForm from "./SignInSignUpForm";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { useRouter } from "next/navigation";
import { Comedian } from "@punchup/data/legacy/comedian";
import { FOLLOW_LOCATIONS } from "@punchup/data/legacy/follows";
import dayjs from "@punchup/utils/dayjs";

interface SignInWrapperProps {
  redirectRoute?: string;
  comedianToFollow?: (Pick<Comedian, "id" | "slug"> | Pick<Comedian, "slug">) &
    Partial<Pick<Comedian, "display_name_short">>;
  followLocation?: FOLLOW_LOCATIONS;
  policyFooter: React.ReactNode;
}

export function SignInWrapper({
  redirectRoute,
  policyFooter,
  comedianToFollow,
  followLocation,
}: SignInWrapperProps) {
  const router = useRouter();
  const supabase = createClientComponentClient();

  const handleAuthComplete = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user) {
      // Validate the created_at timestamp
      const userCreatedAt = dayjs(user.created_at);

      // If the date is invalid, treat as new user to ensure they go through onboarding
      if (!userCreatedAt.isValid()) {
        router.push("/suggested");
        return;
      }

      // Check if user was created in the last 2 minutes
      const now = dayjs();
      const twoMinutesAgo = now.subtract(2, "minutes");
      const isNewUser = userCreatedAt.isAfter(twoMinutesAgo);

      if (isNewUser) {
        // Brand new user, send to onboarding
        router.push("/suggested");
        return;
      }

      // Existing user, send to their intended destination
      if (redirectRoute) {
        router.push(redirectRoute);
      } else {
        router.push("/");
      }
    }
  };

  return (
    <SignInSignUpForm
      redirectTo={redirectRoute}
      policyFooter={policyFooter}
      comedianToFollow={comedianToFollow}
      followLocation={followLocation}
      autoFocus
      handleAuthComplete={handleAuthComplete}
    />
  );
}
